import {
	Button,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useState } from "react";
import { Link } from "react-router";
import { Async, Empty, Json, KV, PageHead, Pager, SearchBox, Segmented } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useDebounced, usePaged } from "@/lib/hooks";

export function AuditPage() {
	const t = useT();
	const { locale } = useI18n();
	const [q, setQ] = useState("");
	const [scope, setScope] = useState<"" | "admin." | "page.">("");
	const [open, setOpen] = useState<import("@/lib/api").AuditEntry | null>(null);
	const query = useDebounced(q);
	const { state, rows, hasMore, loadingMore, more } = usePaged(
		(offset, limit) => api.audit({ q: query, action: scope, limit, offset }),
		(page) => page.entries,
		[query, scope],
		100,
	);

	return (
		<>
			<PageHead title={t("audit.title")} help={t("audit.lede")} />
			<div className="table-tools">
				<SearchBox value={q} onChange={setQ} />
				<Segmented
					value={scope}
					onChange={setScope}
					options={[
						{ value: "", label: t("state.all") },
						{ value: "admin.", label: t("audit.filter.console") },
						{ value: "page.", label: t("audit.filter.panel") },
					]}
				/>
			</div>
			<Async state={state}>
				{() =>
					rows.length === 0 ? (
						<Empty />
					) : (
						<>
							<div className="table-scroll">
								<Table className="audit-table text-[length:var(--t-sm)]">
									<TableHeader>
										<TableRow>
											<TableHead>{t("audit.when")}</TableHead>
											<TableHead>{t("audit.who")}</TableHead>
											<TableHead>{t("audit.action")}</TableHead>
											<TableHead>{t("audit.target")}</TableHead>
											<TableHead>{t("audit.org")}</TableHead>
											<TableHead>{t("audit.detail")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{rows.map((e) => (
											<TableRow key={e.id}>
												<TableCell className="small whitespace-nowrap" title={fmt.dateTime(locale, e.created_at)}>
													{fmt.relative(locale, e.created_at)}
												</TableCell>
												<TableCell>
													{e.actor_user_id ? (
														<Link
															className="cell-summary"
															title={e.actor_email ?? e.actor_user_id}
															to={`/users/${e.actor_user_id}`}
														>
															{e.actor_email ?? e.actor_user_id}
														</Link>
													) : (
														"—"
													)}
												</TableCell>
												<TableCell className="mono cell-clip" title={e.action}>
													{e.action}
												</TableCell>
												<TableCell className="mono cell-clip" title={e.target ?? undefined}>
													{e.target ?? "—"}
												</TableCell>
												<TableCell>
													{e.org_id ? (
														<Link className="cell-summary" title={e.org_name ?? e.org_id} to={`/orgs/${e.org_id}`}>
															{e.org_name ?? e.org_id}
														</Link>
													) : (
														""
													)}
												</TableCell>
												<TableCell>
													{e.detail ? (
														<Button size="sm" variant="ghost" type="button" onClick={() => setOpen(e)}>
															{t("state.open")}
														</Button>
													) : null}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<Pager shown={rows.length} hasMore={hasMore} busy={loadingMore} onMore={more} />
						</>
					)
				}
			</Async>
			{open ? (
				<ChipModal open size="xl" srTitle={t("audit.detail")} onOpenChange={() => setOpen(null)}>
					<ChipModalHeader onClose={() => setOpen(null)}>{t("audit.detail")}</ChipModalHeader>
					<ChipModalBody>
						<KV
							items={[
								[t("audit.when"), fmt.dateTime(locale, open.created_at)],
								[t("audit.who"), open.actor_email ?? open.actor_user_id],
								[t("audit.org"), open.org_name ?? open.org_id],
								[t("audit.action"), <span className="mono">{open.action}</span>],
								[t("audit.target"), <span className="mono">{open.target}</span>],
							]}
						/>
						<Json value={open.detail} />
					</ChipModalBody>
					<ChipModalFooter defaultAction="dismiss" cancelLabel={t("state.close")} onCancel={() => setOpen(null)} />
				</ChipModal>
			) : null}
		</>
	);
}
